import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Grid, Tooltip, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { COACH_PROFILE_ROUTE, COACH_PROFILE_SET_BOOKING_TIME_ROUTE } from '../../utils/routes';
import { getCoachUnavailableDates } from '../../utils/utils';
import { fetchCoachAvailabilitiesForMiniweb } from '../../redux/miniweb/miniwebSlice';
import Calender from '../../components/CalenderSettings/Calender/Calender';
import MiniWebSiteHeader from './MiniWebSiteHeader/MiniWebSiteHeader';
import MiniWebSiteFooter from './MiniWebSiteFooter/MiniWebSiteFooter';

const MiniWebSiteSetBookingDate = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { username } = useParams();
  const { coachAvailabilities, coachFullBookedDays } = useAppSelector((state) => state.miniweb);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const { servicePackage } = location.state || {};
  const [coachUnavailableDays, setCoachUnavailableDays] = useState<Dayjs[]>([]);

  useEffect(() => {
    if (username) {
      dispatch(fetchCoachAvailabilitiesForMiniweb({ username }));
    }
  }, [dispatch, username]);

  useEffect(() => {
    if (username && !servicePackage) {
      navigate(COACH_PROFILE_ROUTE.replace(':username', username));
    }
  }, [servicePackage, username, navigate]);

  useEffect(() => {
    const unavailableDates = getCoachUnavailableDates(coachAvailabilities);
    if (coachFullBookedDays?.length) {
      const fullBookedDays = coachFullBookedDays.map((d) => dayjs(d));
      unavailableDates.push(...fullBookedDays);
    }
    if (unavailableDates.length) {
      setCoachUnavailableDays(unavailableDates);
    }
  }, [coachAvailabilities]);

  const handleNavigateToBookingTime = () => {
    if (username && servicePackage && selectedDate) {
      navigate(COACH_PROFILE_SET_BOOKING_TIME_ROUTE.replace(':username', username), {
        state: {
          servicePackage,
          selectedDate,
        },
      });
    }
  };

  const handleDateChange = (date: Dayjs | null) => {
    setSelectedDate(date);
  };

  const handleNavigateBack = () => {
    navigate(-1);
  };

  return (
    <Grid
      container
      display="flex"
      sx={{
        background: 'linear-gradient(163deg, #FFF 10.13%, #FFF4F0 89.18%)',
        minHeight: '100vh',
        flexDirection: 'column',
      }}
      alignItems="center"
      justifyContent="center"
    >
      <MiniWebSiteHeader />
      {!coachAvailabilities && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {servicePackage && (
        <Grid
          item
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
          sx={{ flexGrow: 1, gap: '18px', marginTop: '36px' }}
        >
          <Box
            sx={{
              width: '361px',
              backgroundColor: '#FFFFFF',
              borderRadius: '10px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                height: '65px',
                borderRadius: '10px 10px 0px 0px',
                borderBottom: '1px solid #EEEEEE',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '20px',
              }}
            >
              <Button
                onClick={handleNavigateBack}
                sx={{
                  width: '10px',
                  height: '24px',
                  background: 'transparent !important',
                }}
              >
                <img src="/assets/icons/back-arrow.svg" alt="back-arrow" height={24} width={24} />
              </Button>
              <Tooltip
                title={servicePackage.title}
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: '#2A3C67',
                      color: 'white',
                    },
                  },
                }}
              >
                <Typography
                  variant="h6"
                  color="#2A3C67"
                  sx={{
                    cursor: 'pointer',
                    flexGrow: 1,
                    marginLeft: '8px',
                    marginRight: '8px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                  }}
                >
                  {servicePackage.title}
                </Typography>
              </Tooltip>
              <Box
                sx={{
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                <img src="/assets/icons/session-price.svg" alt="price" />
                <Typography
                  variant="h6"
                  color="#2A3C67"
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  ${servicePackage.sessionPrice}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: '12px',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h6" color="#2A3C67">
                {t('miniWebSite.booking.selectDate')}
              </Typography>
              <Calender
                isMiniWeb
                username={username}
                disabledDates={coachUnavailableDays}
                onDateChange={handleDateChange}
              />
              <Box
                sx={{
                  height: '46px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: '12px',
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    background:
                      'var(--Brand-Cadet-Blue, linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%))',
                    borderRadius: '15px',
                    '&:hover': {
                      background:
                        'var(--Brand-Cadet-Blue, linear-gradient(138deg, #4e7c91 24.11%, #2d4f5f 83.2%))',
                    },
                  }}
                  onClick={handleNavigateToBookingTime}
                  disabled={!selectedDate}
                >
                  {t('miniWebSite.booking.next')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Grid>
      )}
      <MiniWebSiteFooter />
    </Grid>
  );
};

export default MiniWebSiteSetBookingDate;
